<template>
  <div class="order-list-container">
    <!-- 订单管理头部 - 整合搜索筛选和操作按钮 -->
    <el-card class="order-header-card">
      <div class="header-content">
        <div class="header-left">
          <h2>销售记录</h2>
        </div>

        <!-- 搜索筛选 + 操作按钮 右侧区域 -->
        <div class="header-right">
          <!-- 搜索筛选控件 -->
          <div class="search-filter-group">
            <el-input
                v-model="searchQuery.keyword"
                placeholder="搜索订单号/券码/手机号"
                prefix-icon="el-icon-search"
                style="width: 220px; margin-right: 10px;"
                @keyup.enter.native="handleSearch"
                clearable
                @clear="handleSearch"
                size="small"
            />
            <el-select
                v-model="searchQuery.orderType"
                placeholder="订单类型"
                style="width: 120px; margin-right: 10px;"
                clearable
                @change="handleSearch"
                size="small"
            >
              <el-option label="全部类型" :value="null"/>
              <el-option label="线上购券" :value="1"/>
              <el-option label="线下扫码" :value="2"/>
            </el-select>
            <el-select
                v-model="searchQuery.status"
                placeholder="订单状态"
                style="width: 110px; margin-right: 10px;"
                clearable
                @change="handleSearch"
                size="small"
            >
              <el-option label="全部状态" :value="null"/>
              <el-option label="待支付" :value="1"/>
              <el-option label="已支付" :value="2"/>
              <el-option label="已完成" :value="3"/>
              <el-option label="已退款" :value="6"/>
            </el-select>
            <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                style="width: 240px; margin-right: 15px;"
                size="small"
                :shortcuts="timeShortcuts"
                @change="handleDateChange"
            />
          </div>

          <!-- 操作按钮 -->
          <div class="action-buttons">
            <el-button @click="refreshData" size="small">
              <i class="el-icon-refresh"></i>
              刷新
            </el-button>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 订单表格 -->
    <el-card class="order-table-card">
      <div v-loading="loading">
        <el-table
            :data="orderList"
            style="width: 100%"
            :header-cell-style="{background: '#f5f7fa', color: '#606266'}"
        >
          <el-table-column prop="id" label="订单ID" width="80"/>
          <el-table-column prop="orderNo" label="订单号" min-width="180">
            <template slot-scope="{row}">
              <span class="order-no">{{ row.orderNo }}</span>
            </template>
          </el-table-column>
          <el-table-column label="订单类型" width="100">
            <template slot-scope="{row}">
              <el-tag :type="row.orderType === 1 ? 'primary' : 'warning'" size="small">
                {{ row.orderType === 1 ? '线上购券' : '线下扫码' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="卡券信息" min-width="180">
            <template slot-scope="{row}">
              <div v-if="row.voucherCode || row.voucherName" class="voucher-info">
                <div class="voucher-name">{{ row.voucherName || '--' }}</div>
                <div class="voucher-code" v-if="row.voucherCode">
                  券码: {{ row.voucherCode }}
                </div>
              </div>
              <span v-else>--</span>
            </template>
          </el-table-column>
          <el-table-column prop="payAmount" label="实付金额(元)" width="120" align="right">
            <template slot-scope="{row}">
              <span class="amount">{{ formatPrice(row.payAmount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="90">
            <template slot-scope="{row}">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ getStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="addDataTime" label="创建时间" width="170">
            <template slot-scope="{row}">
              {{ formatTime(row.addDataTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="payTime" label="支付时间" width="170">
            <template slot-scope="{row}">
              {{ row.payTime ? formatTime(row.payTime) : '--' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" fixed="right">
            <template slot-scope="{row}">
              <el-button type="text" size="small" @click="handleViewDetail(row)">
                <i class="el-icon-view"></i>
                详情
              </el-button>
              <el-button
                  v-if="row.status === 2 || row.status === 3"
                  type="text"
                  size="small"
                  @click="handleRefund(row)"
                  style="color: #F56C6C"
              >
                <i class="el-icon-refresh-right"></i>
                退款
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
              :current-page="pagination.currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              :total="pagination.total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 订单详情弹窗 -->
    <el-dialog
        title="订单详情"
        :visible.sync="detailDialogVisible"
        width="650px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        append-to-body
        destroy-on-close
        @close="handleDetailDialogClose"
    >
      <div v-loading="detailLoading" class="detail-dialog-content">
        <div class="detail-section">
          <h4 class="section-title">订单信息</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">订单号</span>
              <span class="value">{{ currentOrder.orderNo }}</span>
            </div>
            <div class="info-item">
              <span class="label">订单类型</span>
              <el-tag :type="currentOrder.orderType === 1 ? 'primary' : 'warning'" size="small">
                {{ currentOrder.orderType === 1 ? '线上购券' : '线下扫码' }}
              </el-tag>
            </div>
            <div class="info-item">
              <span class="label">订单状态</span>
              <el-tag :type="getStatusType(currentOrder.status)" size="small">
                {{ getStatusText(currentOrder.status) }}
              </el-tag>
            </div>
            <div class="info-item">
              <span class="label">实付金额</span>
              <span class="value price">¥{{ formatPrice(currentOrder.payAmount) }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建时间</span>
              <span class="value">{{ formatTime(currentOrder.addDataTime) }}</span>
            </div>
            <div class="info-item">
              <span class="label">支付时间</span>
              <span class="value">{{ currentOrder.payTime ? formatTime(currentOrder.payTime) : '--' }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="currentOrder.voucherCode || currentOrder.voucherName">
          <h4 class="section-title">卡券信息</h4>
          <div class="info-grid">
            <div class="info-item" v-if="currentOrder.voucherName">
              <span class="label">券名称</span>
              <span class="value">{{ currentOrder.voucherName }}</span>
            </div>
            <div class="info-item" v-if="currentOrder.voucherCode">
              <span class="label">券码</span>
              <span class="value voucher-code-text">{{ currentOrder.voucherCode }}</span>
            </div>
            <div class="info-item" v-if="currentOrder.voucherStatus !== undefined && currentOrder.voucherStatus !== null">
              <span class="label">券状态</span>
              <el-tag :type="getVoucherStatusType(currentOrder.voucherStatus)" size="small">
                {{ getVoucherStatusText(currentOrder.voucherStatus) }}
              </el-tag>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="currentOrder.remark">
          <h4 class="section-title">备注</h4>
          <div class="remark-content">{{ currentOrder.remark }}</div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button
            v-if="currentOrder.status === 2 || currentOrder.status === 3"
            type="danger"
            size="small"
            @click="handleRefundFromDetail"
        >
          申请退款
        </el-button>
        <el-button @click="detailDialogVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 退款弹窗 -->
    <el-dialog
        title="申请退款"
        :visible.sync="refundDialogVisible"
        width="450px"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        append-to-body
    >
      <div class="refund-dialog-content">
        <el-form label-width="80px" size="small">
          <el-form-item label="订单号">
            <span>{{ refundOrder.orderNo }}</span>
          </el-form-item>
          <el-form-item label="支付金额">
            <span class="refund-amount">¥{{ formatPrice(refundOrder.payAmount) }}</span>
          </el-form-item>
          <el-form-item label="退款金额">
            <el-input-number
                v-model="refundAmount"
                :min="0.01"
                :max="maxRefundAmount"
                :precision="2"
                :step="0.01"
                style="width: 200px"
            />
            <span style="margin-left: 8px; color: #909399; font-size: 12px;">元</span>
          </el-form-item>
          <el-form-item label="退款原因">
            <el-input
                v-model="refundReason"
                type="textarea"
                :rows="3"
                placeholder="请输入退款原因"
                maxlength="80"
                show-word-limit
            />
          </el-form-item>
        </el-form>

        <!-- ✅ 新增：温馨提示 -->
        <el-alert
            title="温馨提示"
            type="info"
            :closable="false"
            style="margin-top: 10px;"
        >
          <div style="font-size: 12px; line-height: 1.6;">
            · 退款将原路返回到用户支付账户<br>
            · 退款成功后订单状态将自动变更为"已退款"<br>
            · 测试数据可能无法退款，请确认订单已真实支付
          </div>
        </el-alert>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="refundDialogVisible = false">取 消</el-button>
        <el-button type="danger" :loading="refundLoading" @click="confirmRefund">确认退款</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from "@/utils/api"

export default {
  name: 'OrderList',
  data() {
    return {
      loading: false,
      storeId: null,

      searchQuery: {
        keyword: '',
        orderType: null,
        status: null
      },
      dateRange: [],

      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },

      orderList: [],

      detailDialogVisible: false,
      detailLoading: false,
      currentOrder: {},

      refundDialogVisible: false,
      refundLoading: false,
      refundOrder: {},
      refundAmount: 0,
      refundReason: '',

      timeShortcuts: [
        {
          text: '今天',
          value: () => {
            const start = new Date()
            start.setHours(0, 0, 0, 0)
            const end = new Date()
            end.setHours(23, 59, 59, 999)
            return [start, end]
          }
        },
        {
          text: '昨天',
          value: () => {
            const start = new Date()
            start.setDate(start.getDate() - 1)
            start.setHours(0, 0, 0, 0)
            const end = new Date()
            end.setDate(end.getDate() - 1)
            end.setHours(23, 59, 59, 999)
            return [start, end]
          }
        },
        {
          text: '最近7天',
          value: () => {
            const start = new Date()
            start.setDate(start.getDate() - 6)
            start.setHours(0, 0, 0, 0)
            const end = new Date()
            end.setHours(23, 59, 59, 999)
            return [start, end]
          }
        },
        {
          text: '最近30天',
          value: () => {
            const start = new Date()
            start.setDate(start.getDate() - 29)
            start.setHours(0, 0, 0, 0)
            const end = new Date()
            end.setHours(23, 59, 59, 999)
            return [start, end]
          }
        }
      ]
    }
  },

  computed: {
    maxRefundAmount() {
      const amount = this.refundOrder.payAmount || 0
      return (amount / 100).toFixed(2)
    }
  },

  mounted() {
    this.initData()
  },

  methods: {
    async initData() {
      await this.waitForStoreId()
      if (!this.storeId) {
        this.$message.warning('未获取到店铺信息，请联系管理员')
        return
      }
      await this.loadOrderList()
    },

    waitForStoreId() {
      this.getStoreIdFromVuex()
      if (this.storeId) return Promise.resolve()
      return new Promise((resolve) => {
        let attempts = 0
        const timer = setInterval(() => {
          this.getStoreIdFromVuex()
          attempts++
          if (this.storeId || attempts >= 30) {
            clearInterval(timer)
            resolve()
          }
        }, 100)
      })
    },

    getStoreIdFromVuex() {
      const adminInfo = this.$store.state.adminInfo
      if (adminInfo && adminInfo.merchantId) {
        this.storeId = adminInfo.merchantId
      }
    },

    async loadOrderList() {
      this.loading = true
      try {
        const params = {
          merchantId: this.storeId,
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          keyword: this.searchQuery.keyword || undefined,
          orderType: this.searchQuery.orderType || undefined,
          status: this.searchQuery.status || undefined
        }

        // 时间范围
        if (this.dateRange && this.dateRange.length === 2) {
          params.startTime = this.dateRange[0].getTime()
          params.endTime = this.dateRange[1].getTime()
        }

        const res = await api.order_getOrderList(params, null)
        if (res.data.code === '000') {
          const data = res.data.data
          this.orderList = data.list || data.records || data || []
          this.pagination.total = data.totalCount || data.total || this.orderList.length
        }
      } catch (e) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },

    formatPrice(price) {
      if (!price) return '0.00'
      return (price / 100).toFixed(2)
    },

    formatTime(timestamp) {
      if (!timestamp) return '--'
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    getStatusText(status) {
      const map = {
        1: '待支付',
        2: '已支付',
        3: '已完成',
        4: '已取消',
        5: '退款中',
        6: '已退款'
      }
      return map[status] || '未知'
    },

    getStatusType(status) {
      const map = {
        1: 'info',
        2: 'success',
        3: 'primary',
        4: 'info',
        5: 'warning',
        6: 'danger'
      }
      return map[status] || 'info'
    },

    getVoucherStatusText(status) {
      const map = {
        0: '待使用',
        1: '已使用',
        2: '已过期',
        3: '已退款'
      }
      return map[status] || '未知'
    },

    getVoucherStatusType(status) {
      const map = {
        0: 'warning',
        1: 'success',
        2: 'info',
        3: 'danger'
      }
      return map[status] || 'info'
    },

    handleSearch() {
      this.pagination.currentPage = 1
      this.loadOrderList()
    },

    handleDateChange() {
      this.pagination.currentPage = 1
      this.loadOrderList()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.loadOrderList()
    },

    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadOrderList()
    },

    refreshData() {
      this.searchQuery = { keyword: '', orderType: null, status: null }
      this.dateRange = []
      this.pagination.currentPage = 1
      this.loadOrderList()
    },

    async handleViewDetail(row) {
      this.detailDialogVisible = true
      this.detailLoading = true
      try {
        const res = await api.order_getOrderDetail({ id: row.id })
        if (res.data.code === '000') {
          this.currentOrder = res.data.data
        }
      } catch (e) {
        this.$message.error('加载失败')
      } finally {
        this.detailLoading = false
      }
    },

    handleDetailDialogClose() {
      this.currentOrder = {}
    },

    handleRefund(row) {
      this.refundOrder = row
      this.refundAmount = this.formatPrice(row.payAmount)
      this.refundReason = ''
      this.refundDialogVisible = true
    },

    handleRefundFromDetail() {
      this.refundOrder = this.currentOrder
      this.refundAmount = this.formatPrice(this.currentOrder.payAmount)
      this.refundReason = ''
      this.refundDialogVisible = true
    },

    async confirmRefund() {
      // ✅ 修复：trim() 是方法，不是属性
      if (!this.refundReason || this.refundReason.trim() === '') {
        this.$message.warning('请输入退款原因')
        return
      }

      this.refundLoading = true
      try {
        const params = {
          outTradeNo: this.refundOrder.orderNo,
          refundAmount: Math.round(this.refundAmount * 100),
          refundReason: this.refundReason
        }
        const res = await api.order_refund(params, null)

        if (res.data.code === '000') {
          this.$message.success('退款申请成功')
          this.refundDialogVisible = false
          this.loadOrderList()
        } else {
          // ✅ 新增：后端返回失败，友好提示
          this.handleRefundError(res.data.message)
        }
      } catch (e) {
        // ✅ 新增：网络异常友好提示
        this.$message.error('退款请求失败，请稍后重试')
      } finally {
        this.refundLoading = false
      }
    },

    /**
     * ✅ 新增：退款错误处理
     */
    handleRefundError(message) {
      const msg = message || '退款失败'

      // 订单在微信侧不存在
      if (msg.includes('ORDER_NOT_EXIST') || msg.includes('订单不存在')) {
        this.$alert(
            '该订单在微信支付系统中不存在，无法退款。\n\n可能原因：\n· 订单为测试数据，未通过微信支付真实下单\n· 订单已超过退款有效期（1年）\n· 订单号不正确',
            '无法退款',
            {
              confirmButtonText: '知道了',
              type: 'warning'
            }
        )
        return
      }

      // 商户号错误
      if (msg.includes('商户号格式错误') || msg.includes('PARAM_ERROR')) {
        this.$alert(
            '商户号配置错误，无法完成退款。\n请联系系统管理员检查微信支付配置。',
            '配置错误',
            {
              confirmButtonText: '知道了',
              type: 'error'
            }
        )
        return
      }

      // 签名错误
      if (msg.includes('签名') || msg.includes('SIGN')) {
        this.$alert(
            '签名验证失败，无法完成退款。\n请联系系统管理员检查微信支付证书配置。',
            '签名错误',
            {
              confirmButtonText: '知道了',
              type: 'error'
            }
        )
        return
      }

      // 其他错误
      this.$message.error(msg)
    }
  }
}
</script>

<style scoped lang="scss">
.order-list-container {
  padding: 20px;
}

.order-header-card {
  margin-bottom: 20px;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
  }

  .header-left {
    flex: 0 0 auto;

    h2 {
      margin: 0;
      font-size: 24px;
      color: #303133;
    }
  }

  .header-right {
    flex: 1 1 auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;

    .search-filter-group {
      display: flex;
      align-items: center;
      background: #f8f9fa;
      padding: 6px 12px;
      border-radius: 4px;
      border: 1px solid #e9ecef;
    }

    .action-buttons {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
}

.order-table-card {
  .order-no {
    font-weight: 500;
    color: #409eff;
  }

  .voucher-info {
    .voucher-name {
      font-weight: 500;
      margin-bottom: 2px;
    }

    .voucher-code {
      font-size: 12px;
      color: #909399;
      font-family: monospace;
    }
  }

  .amount {
    font-weight: 600;
    color: #f56c6c;
  }
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.detail-dialog-content {
  max-height: 50vh;
  overflow-y: auto;
  padding-right: 8px;

  .detail-section {
    margin-bottom: 16px;

    .section-title {
      margin: 0 0 10px 0;
      padding-bottom: 8px;
      border-bottom: 1px solid #ebeef5;
      font-size: 14px;
      color: #303133;
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    .info-item {
      display: flex;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid #f5f7fa;

      .label {
        width: 80px;
        color: #909399;
        font-size: 13px;
        flex-shrink: 0;
      }

      .value {
        color: #303133;
        font-size: 14px;

        &.price {
          font-size: 18px;
          font-weight: bold;
          color: #f56c6c;
        }

        &.voucher-code-text {
          font-family: monospace;
          font-size: 13px;
          color: #409eff;
        }
      }
    }
  }

  .remark-content {
    padding: 10px;
    line-height: 1.6;
    color: #606266;
    font-size: 14px;
    background-color: #fafbfc;
    border-radius: 4px;
    white-space: pre-wrap;
    word-break: break-all;
  }
}

.refund-dialog-content {
  .refund-amount {
    font-size: 16px;
    font-weight: bold;
    color: #f56c6c;
  }
}

@media (max-width: 1400px) {
  .order-header-card {
    .header-content {
      flex-wrap: wrap;
    }

    .header-right {
      width: 100%;
      margin-top: 15px;
      justify-content: flex-start;
      flex-wrap: wrap;
    }
  }
}

@media (max-width: 768px) {
  .order-list-container {
    padding: 12px;
  }

  .detail-dialog-content {
    .info-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>